import { $ } from '../platform/dom';
import { elRw, elSlots } from '../platform/dom';
import { RNG } from '../core/rng';
import { MODULES } from '../data/modules';
import { SFX } from '../platform/audio';
import { META } from '../platform/storage';
import { STATE } from '../sim/fx';
import { banner } from './hud';
import { P } from '../world/player';
import { pedestal, portal } from '../world/pools';
import { resetFocus, setFocus } from './screens/router';

/* ============================================================
   獎勵：補給台的行為模組

   M3 移除了整個數值層（經驗值、等級、升級畫面、18 種數值強化、4 種數值核心）。
   理由：0.2 的成長要全部來自房間獎勵，那些數值卡在 M5 本來就會整批刪掉，
   而熱量制讓其中兩張（增壓電容減冷卻、延長迴路加持續）當場失效 ——
   與其花力氣改語意去救一個兩個里程碑後要刪的東西，不如現在就拔。

   保留的是**行為模組**：它們是 0.2 的正確方向（M6 只是重寫實作、不是砍概念），
   而且跟熱量有真實交互 —— 例如貫穿彈芯改變了打不打得到，連帶改變產熱節奏。

   成長暫時由「每清空一階段自動給固定強化」代替，見 sim/stage-flow.ts 的 stageBuff()。
   ============================================================ */

export var currentRewards = [];

/* 升級卡片。

   舊版是「標籤／標題／描述／等級」四行純文字 —— 使用者的回饋是
   「記憶點不強」，而那是對的：三張卡並排時眼睛只能靠讀字去分辨，
   在一個要**暫停遊戲做選擇**的介面上，這個成本太高。

   新版多三樣東西，每一樣都對應一個玩家真的要回答的問題：

     圖示    「這是什麼」—— 一眼可辨，不用讀字
     軸標籤  「它跟我現在的 build 合不合」（VS 規則三：武裝只吃 2–3 條軸）
     質變提示「下一級會不會變一把武器」（VS 規則二：量變→質變門檻）

   `desc` 支援 `**粗體**` —— 質變的那一句要在描述裡就跳出來。 */
export function card(i, tag, title, desc, note?, cls?, opt?: any) {
  opt = opt || {};
  var ico = opt.icon
    ? '<span class="cico"><img src="' + opt.icon + '" alt=""></span>'
    : '';
  var axes = (opt.axes && opt.axes.length)
    ? '<span class="caxes">' + opt.axes.map(function (a) {
      return '<i class="cax">' + a + '</i>';
    }).join('') + '</span>'
    : '';
  /* 質變提示只在「下一級就會發生」時出現。每一級都掛著的話它就變成背景，
     玩家不會為了它調整選擇 —— 而那正是這個提示存在的唯一理由。 */
  var turn = opt.turn
    ? '<span class="cturn">下一級質變 · ' + opt.turn + '</span>'
    : '';
  /* **進化前置達成的標記。** `03-in-run-progression.md` 的原話是
     「這是最重要的引導」—— 而現在進化只從寶箱出，玩家更沒有別的管道知道
     自己差一級就湊齊了。發光邊框由 `.card.pre` 出（見 style.css）。 */
  var pre = opt.pre
    ? '<span class="cpre">拿了就湊齊 · ' + opt.pre + '</span>'
    : '';
  /* 破格協定的代價。**跟好處分開一行、用紅字** ——
     混在同一段描述裡的話它會被當成加成的一部分讀過去，
     而「看不到代價的破格效果等於沒有代價」（`data/protocols.ts`）。 */
  var cost = opt.cost
    ? '<span class="ccost">代價 · ' + opt.cost + '</span>'
    : '';
  /* Banish 的 ✕。**做在卡片上而不是做成模式切換**：切模式要先按一個鍵
     再點卡，而玩家按下 Banish 的那一刻心裡想的已經是「就是這張」。
     `data-b` 讓 `bindCards` 分得出這一次點擊是選它還是排除它。 */
  /* ✕ 放**右下角**。第一版擺在右上、緊鄰數字鍵標籤，實看截圖上
     「✕」和「1」黏成一組，讀起來像「×1」（乘以一）—— 那是量詞不是按鈕。 */
  var ban = opt.ban
    ? '<i class="cban" data-b="' + i + '" title="排除這個選項（本局不再出現）">✕</i>'
    : '';
  /* 等級點陣。`03-in-run-progression.md` 的 UI 要求是「顯示當前等級 → 下一等級」，
     而文字版（「Lv2 → Lv3」）要讀兩個數字再相減才知道「還剩幾級」。
     點陣一眼就答得出來，滿級也一眼看得出來（滿級是進化的前提）。 */
  var pips = '';
  if (opt.pips && opt.pips.max > 1) {
    for (var q = 0; q < opt.pips.max; q++) {
      pips += '<i class="' + (q < opt.pips.lv ? 'on' : (q === opt.pips.lv ? 'next' : '')) + '"></i>';
    }
    pips = '<span class="cpips">' + pips + '</span>';
  }
  /* **稀有度主導顏色**（2026-08-05 使用者的決定，推翻 08-04 的「形式不用顏色」）。
     08-04 的理由是「顏色被類別用光了」，08-05 的判準壓過它：混戰中
     「這張很大」必須一公尺外讀得出來，而三道 2px 的斜槓做不到。
     類別退到**圖示色相、左緣 2px 條與 chip**（見 styles/cards.css）。

     沒有稀有度的卡（Boss 擊破獎勵、寶箱直接給的進化）不寫稀有度文字，
     但仍要有主色：進化與協定當傳說看，機體強化用自己的橘。 */
  var RNAME: Record<string, string> = { common: '普通', rare: '稀有', epic: '史詩', legendary: '傳說' };
  var rar = opt.rar || (cls === 'evo' || cls === 'prot' ? 'legendary' : (cls === 'mw' ? 'mw' : 'common'));
  var top = '<span class="ctop">' +
    (opt.rar ? '<span class="crar">' + RNAME[opt.rar] + '</span>' : '') +
    '<span class="ck">' + (i + 1) + '</span></span>';
  /* 版型（2026-08-04 晚重做）：**圖示放大移到左側、文字靠右**。
     舊版是「小圖示＋標籤」一行、標題一行、描述一行的直式堆疊，
     三張並排時眼睛要由上往下掃三次。橫式之後圖示排成一列，
     第一眼掃過去就知道「這三張分別是什麼東西」，再回頭讀想要的那張。 */
  /* 稀有度標與數字鍵**排在第一行的右端，不用絕對定位** ——
     絕對定位的版本要靠 padding 幫類別標籤讓位，而 1024 以下那個 padding
     會把「規則 · Lv1」擠成兩行（實看截圖抓到）。同一行 flex 就沒有這個問題。 */
  return '<button class="card focusable r-' + rar + ' k-' + (cls || 'mod') +
    (opt.pre ? ' pre' : '') + '" data-i="' + i + '">' +
    '<i class="cedge"></i>' +
    '<span class="chead">' + ico +
    '<span class="cmain"><span class="crow"><span class="ctag">' + tag + '</span>' + top + '</span>' +
    '<h3>' + title + '</h3>' + pips + '</span></span>' +
    '<p>' + bold(desc) + '</p>' + cost + axes + turn + pre +
    '<span class="clv">' + note + '</span>' + ban + '</button>';
}
/** 描述裡的 `**…**` 轉成強調。武裝的質變那一句靠它跳出來。 */
function bold(s) {
  return String(s).replace(/\*\*(.+?)\*\*/g, '<b class="cem">$1</b>');
}

export function bindCards(root, fn, onBan?) {
  var bs = root.querySelectorAll('.card');
  for (var i = 0; i < bs.length; i++) {
    /* 滑鼠移過去也要把**鍵盤焦點**移過去。兩套高亮並存的話，
       玩家分不清「我現在按 Enter 會選到哪一張」。 */
    (function (b, idx) {
      b.addEventListener('click', function () { fn(parseInt(b.getAttribute('data-i'), 10)); });
      if (b.addEventListener) b.addEventListener('mouseenter', function () { setFocus(idx); });
    })(bs[i], i);
  }
  resetFocus();
  if (!onBan) return;
  /* ✕ 在卡片**裡面**，所以一定要 `stopPropagation` ——
     不擋的話事件冒泡上去會同時「排除它」又「選了它」。 */
  var xs = root.querySelectorAll('.cban');
  for (var j = 0; j < xs.length; j++) {
    (function (x) {
      x.addEventListener('click', function (e) {
        e.stopPropagation();
        onBan(parseInt(x.getAttribute('data-b'), 10));
      });
    })(xs[j]);
  }
}

export function openReward() {
  STATE.reward = true;
  pedestal.active = false; pedestal.g.visible = false;
  var mech = P.mech, mods = [];
  MODULES.forEach(function (m) {
    if (m.w !== mech.primary && m.w !== mech.mid && m.w !== mech.ult) return;
    if (m.t2 && !META.tier2) return;
    if (P.mod[m.slot]) return;
    mods.push(m);
  });
  currentRewards = [];
  while (mods.length && currentRewards.length < 3) {
    var i2 = Math.floor(RNG.next() * mods.length);
    currentRewards.push({ kind: 'mod', m: mods[i2] }); mods.splice(i2, 1);
  }
  // 三個槽位都裝滿之後就沒有模組可給了，直接開門，不要卡住玩家
  if (!currentRewards.length) {
    STATE.reward = false; portal.active = true; SFX.portal();
    banner('模組槽已滿');
    return;
  }
  var html = '';
  currentRewards.forEach(function (r, i) {
    html += card(i, '模組 · ' + slotName(r.m.slot), r.m.n, r.m.d, '改變運作方式', 'mod');
  });
  $('rwCards').innerHTML = html;
  bindCards($('rwCards'), chooseReward);
  elRw.classList.add('show'); SFX.level();
}

export function slotName(s) { return s === 'primary' ? '主武裝' : (s === 'mid' ? '小招' : '大招'); }

export function chooseReward(i) {
  if (!STATE.reward || !currentRewards[i]) return;
  var r = currentRewards[i];
  P.mod[r.m.slot] = r.m.id; banner('模組載入 · ' + r.m.n);
  elRw.classList.remove('show'); STATE.reward = false;
  portal.active = true;
  updateSlots();
  SFX.portal();
}

export function updateSlots() {
  var out = '';
  ['primary', 'mid', 'ult'].forEach(function (s) {
    var id = P.mod[s], name = '—';
    if (id) for (var i = 0; i < MODULES.length; i++) if (MODULES[i].id === id) name = MODULES[i].n;
    out += '<div class="slot' + (id ? ' on' : '') + '"><span>' + slotName(s) + '</span><b>' + name + '</b></div>';
  });
  elSlots.innerHTML = out;
}
